import React, { useState, useEffect } from "react";
import axios from "axios";
import { useBusinessPlan } from "../App";
import { useCurrency } from "../contexts/CurrencyContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Textarea } from "./ui/textarea";
import { Separator } from "./ui/separator";
import { Progress } from "./ui/progress";
import ExportButton from "./ui/ExportButton";
// Using alert instead of toast for compatibility
import { 
  Calculator,
  TrendingDown,
  DollarSign,
  BarChart3,
  LineChart,
  PieChart,
  Target,
  Zap,
  Activity,
  RefreshCw,
  Download,
  Eye,
  Settings,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  Percent
} from "lucide-react";
import { 
  LineChart as RechartsLineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  ResponsiveContainer, 
  BarChart, 
  Bar,
  AreaChart,
  Area,
  ComposedChart
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EnhancedFinancialCalculations = () => {
  const { currentPlan } = useBusinessPlan();
  const { formatCurrency: formatCurrencyContext } = useCurrency();
  const [loading, setLoading] = useState(false);
  const [activeCalculation, setActiveCalculation] = useState("depreciation");
  
  // Calculation states
  const [depreciationInputs, setDepreciationInputs] = useState({
    method_type: "straight_line",
    asset_cost: 100000,
    useful_life_years: 5,
    salvage_value: 10000,
    depreciation_rate: 0.4,
    production_capacity: 10000
  });
  const [depreciationResults, setDepreciationResults] = useState(null);

  const [roiInputs, setRoiInputs] = useState({
    methodology_type: "simple_roi",
    investment_amount: 50000,
    cash_flows: [15000, 20000, 25000, 18000, 22000],
    discount_rate: 0.10,
    reinvestment_rate: 0.08,
    finance_rate: 0.06
  });
  const [roiResults, setRoiResults] = useState(null);

  const [breakEvenInputs, setBreakEvenInputs] = useState({
    analysis_type: "unit_breakeven",
    fixed_costs: 50000,
    variable_cost_per_unit: 20,
    selling_price_per_unit: 50,
    target_profit: 0
  });
  const [breakEvenResults, setBreakEvenResults] = useState(null);

  const [waccInputs, setWaccInputs] = useState({
    equity_value: 1000000,
    debt_value: 500000,
    cost_of_equity: 0.12,
    cost_of_debt: 0.06,
    tax_rate: 0.25,
    risk_free_rate: 0.03,
    market_risk_premium: 0.08,
    beta: 1.2
  });
  const [waccResults, setWaccResults] = useState(null);
  
  // Financial Management Integration
  const [integrationData, setIntegrationData] = useState(null);
  const [useIntegratedData, setUseIntegratedData] = useState(true);

  // Format currency with context
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return formatCurrencyContext(0, currentPlan?.currency || 'USD');
    const compact = Math.abs(amount) >= 1000000;
    return formatCurrencyContext(amount, currentPlan?.currency || 'USD', null, compact);
  };

  const formatPercentage = (percent) => {
    return `${(percent || 0).toFixed(2)}%`;
  };

  const formatNumber = (num) => {
    return (num || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Calculation functions
  const calculateDepreciation = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/enhanced-calculations/depreciation`, depreciationInputs);
      setDepreciationResults(response.data);
      alert("Depreciation calculated successfully!");
    } catch (error) {
      console.error("Error calculating depreciation:", error);
      alert("Failed to calculate depreciation: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const calculateROI = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/enhanced-calculations/roi-analysis`, roiInputs);
      setRoiResults(response.data);
      alert("ROI analysis completed successfully!");
    } catch (error) {
      console.error("Error calculating ROI:", error);
      alert("Failed to calculate ROI: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const calculateBreakEven = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/enhanced-calculations/break-even`, breakEvenInputs);
      setBreakEvenResults(response.data);
      alert("Break-even analysis completed successfully!");
    } catch (error) {
      console.error("Error calculating break-even:", error);
      alert("Failed to calculate break-even: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const calculateWACC = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/enhanced-calculations/wacc`, waccInputs);
      setWaccResults(response.data);
      alert("WACC calculated successfully!");
    } catch (error) {
      console.error("Error calculating WACC:", error);
      alert("Failed to calculate WACC: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const getCalculationIcon = (type) => {
    switch (type) {
      case "depreciation": return <TrendingDown className="h-4 w-4" />;
      case "roi": return <TrendingUp className="h-4 w-4" />;
      case "breakeven": return <Target className="h-4 w-4" />;
      case "wacc": return <Percent className="h-4 w-4" />;
      default: return <Calculator className="h-4 w-4" />;
    }
  };

  const getDecisionColor = (decision) => {
    if (decision === "Accept") return "text-green-600 bg-green-50 border-green-200";
    if (decision === "Reject") return "text-red-600 bg-red-50 border-red-200";
    return "text-gray-600 bg-gray-50 border-gray-200";
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Enhanced Financial Calculations
          </h1>
          <p className="text-gray-600">
            Advanced financial analysis with multiple methodologies, depreciation models, and sensitivity analysis
          </p>
        </div>
        <ExportButton
          targetId="financial-calculations-content"
          componentName="Financial_Calculations"
          formats={['png', 'xlsx']}
          size="sm"
          variant="outline"
          showIcon={true}
          showText={true}
        />
      </div>
      
      <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded">
        <p className="text-green-700 font-medium">
          ✅ Enhanced Financial Calculations with 4 advanced modules
        </p>
      </div>

      <div id="financial-calculations-content">
        {/* Navigation Tabs */}
        <Tabs value={activeCalculation} onValueChange={setActiveCalculation} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="depreciation" className="flex items-center gap-2">
            <TrendingDown className="h-4 w-4" />
            Depreciation
          </TabsTrigger>
          <TabsTrigger value="roi" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            ROI Analysis
          </TabsTrigger>
          <TabsTrigger value="breakeven" className="flex items-center gap-2">
            <Target className="h-4 w-4" />
            Break-Even
          </TabsTrigger>
          <TabsTrigger value="wacc" className="flex items-center gap-2">
            <Percent className="h-4 w-4" />
            WACC
          </TabsTrigger>
        </TabsList>

        {/* Depreciation Analysis */}
        <TabsContent value="depreciation" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Inputs */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <TrendingDown className="h-5 w-5 mr-2" />
                  Depreciation Parameters
                </CardTitle>
                <CardDescription>
                  Configure asset depreciation calculation method and parameters
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Depreciation Method</Label>
                  <Select 
                    value={depreciationInputs.method_type} 
                    onValueChange={(value) => setDepreciationInputs({...depreciationInputs, method_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="straight_line">Straight-Line</SelectItem>
                      <SelectItem value="declining_balance">Declining Balance</SelectItem>
                      <SelectItem value="sum_of_years">Sum of Years' Digits</SelectItem>
                      <SelectItem value="units_of_production">Units of Production</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Asset Cost ($)</Label>
                    <Input
                      type="number"
                      value={depreciationInputs.asset_cost}
                      onChange={(e) => setDepreciationInputs({...depreciationInputs, asset_cost: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Useful Life (years)</Label>
                    <Input
                      type="number"
                      value={depreciationInputs.useful_life_years}
                      onChange={(e) => setDepreciationInputs({...depreciationInputs, useful_life_years: parseInt(e.target.value) || 0})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Salvage Value ($)</Label>
                    <Input
                      type="number"
                      value={depreciationInputs.salvage_value}
                      onChange={(e) => setDepreciationInputs({...depreciationInputs, salvage_value: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                  {depreciationInputs.method_type === "declining_balance" && (
                    <div>
                      <Label>Depreciation Rate</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={depreciationInputs.depreciation_rate}
                        onChange={(e) => setDepreciationInputs({...depreciationInputs, depreciation_rate: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                  )}
                </div>

                <Button onClick={calculateDepreciation} disabled={loading} className="w-full">
                  <Calculator className="h-4 w-4 mr-2" />
                  {loading ? "Calculating..." : "Calculate Depreciation"}
                </Button>
              </CardContent>
            </Card>

            {/* Results */}
            {depreciationResults && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <BarChart3 className="h-5 w-5 mr-2" />
                    Depreciation Results
                  </CardTitle>
                  <CardDescription>
                    {depreciationResults.results.method} depreciation analysis
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-3 bg-blue-50 rounded border">
                        <div className="text-lg font-bold text-blue-600">
                          {formatCurrency(depreciationResults.results.annual_depreciation || depreciationResults.results.total_depreciation / depreciationInputs.useful_life_years)}
                        </div>
                        <div className="text-xs text-gray-600">Annual Depreciation</div>
                      </div>
                      <div className="text-center p-3 bg-green-50 rounded border">
                        <div className="text-lg font-bold text-green-600">
                          {formatCurrency(depreciationResults.results.total_depreciation)}
                        </div>
                        <div className="text-xs text-gray-600">Total Depreciation</div>
                      </div>
                    </div>

                    {depreciationResults.results.depreciation_schedule && (
                      <div>
                        <h4 className="font-semibold mb-3">Depreciation Schedule</h4>
                        <div className="max-h-60 overflow-y-auto">
                          <div className="space-y-2">
                            {depreciationResults.results.depreciation_schedule.map((item, index) => (
                              <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded text-sm">
                                <span>Year {item.year}</span>
                                <div className="flex gap-4">
                                  <span>{formatCurrency(item.depreciation_expense)}</span>
                                  <span className="text-gray-500">{formatCurrency(item.book_value)}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* ROI Analysis */}
        <TabsContent value="roi" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Inputs */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <TrendingUp className="h-5 w-5 mr-2" />
                  ROI Analysis Parameters
                </CardTitle>
                <CardDescription>
                  Configure return on investment calculation methodology
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>ROI Methodology</Label>
                  <Select 
                    value={roiInputs.methodology_type} 
                    onValueChange={(value) => setRoiInputs({...roiInputs, methodology_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="simple_roi">Simple ROI</SelectItem>
                      <SelectItem value="annualized_roi">Annualized ROI</SelectItem>
                      <SelectItem value="irr">Internal Rate of Return (IRR)</SelectItem>
                      <SelectItem value="mirr">Modified IRR (MIRR)</SelectItem>
                      <SelectItem value="profit_index">Profitability Index</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Initial Investment ($)</Label>
                  <Input
                    type="number"
                    value={roiInputs.investment_amount}
                    onChange={(e) => setRoiInputs({...roiInputs, investment_amount: parseFloat(e.target.value) || 0})}
                  />
                </div>

                <div>
                  <Label>Annual Cash Flows ($)</Label>
                  <Textarea
                    placeholder="Enter annual cash flows separated by commas (e.g., 15000, 20000, 25000)"
                    value={roiInputs.cash_flows.join(", ")}
                    onChange={(e) => {
                      const flows = e.target.value.split(",").map(v => parseFloat(v.trim()) || 0);
                      setRoiInputs({...roiInputs, cash_flows: flows});
                    }}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Discount Rate (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={roiInputs.discount_rate * 100}
                      onChange={(e) => setRoiInputs({...roiInputs, discount_rate: (parseFloat(e.target.value) || 0) / 100})}
                    />
                  </div>
                  {roiInputs.methodology_type === "mirr" && (
                    <div>
                      <Label>Reinvestment Rate (%)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={roiInputs.reinvestment_rate * 100}
                        onChange={(e) => setRoiInputs({...roiInputs, reinvestment_rate: (parseFloat(e.target.value) || 0) / 100})}
                      />
                    </div>
                  )}
                </div>

                <Button onClick={calculateROI} disabled={loading} className="w-full">
                  <Calculator className="h-4 w-4 mr-2" />
                  {loading ? "Calculating..." : "Calculate ROI"}
                </Button>
              </CardContent>
            </Card>

            {/* Results */}
            {roiResults && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <LineChart className="h-5 w-5 mr-2" />
                    ROI Analysis Results
                  </CardTitle>
                  <CardDescription>
                    {roiInputs.methodology_type.replace('_', ' ').toUpperCase()} analysis results
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      {Object.entries(roiResults.results).map(([key, value]) => {
                        if (typeof value === 'number' && !key.includes('decision') && !key.includes('criterion')) {
                          return (
                            <div key={key} className="text-center p-3 bg-blue-50 rounded border">
                              <div className="text-lg font-bold text-blue-600">
                                {key.includes('percentage') || key.includes('roi') || key.includes('rate') ? 
                                  formatPercentage(value) : 
                                  key.includes('period') ? `${formatNumber(value)} years` :
                                  formatCurrency(value)}
                              </div>
                              <div className="text-xs text-gray-600 capitalize">
                                {key.replace(/_/g, ' ')}
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>

                    {roiResults.results.decision_criterion && (
                      <div className={`p-3 rounded border ${getDecisionColor(roiResults.results.decision_criterion)}`}>
                        <div className="flex items-center gap-2">
                          {roiResults.results.decision_criterion === "Accept" ? 
                            <CheckCircle className="h-4 w-4" /> : 
                            <AlertTriangle className="h-4 w-4" />}
                          <span className="font-semibold">Investment Decision: {roiResults.results.decision_criterion}</span>
                        </div>
                      </div>
                    )}

                    {roiResults.sensitivity_analysis && (
                      <div>
                        <h4 className="font-semibold mb-3">Sensitivity Analysis</h4>
                        <div className="max-h-40 overflow-y-auto space-y-2">
                          {Object.entries(roiResults.sensitivity_analysis).map(([scenario, data]) => (
                            <div key={scenario} className="flex justify-between p-2 bg-gray-50 rounded text-sm">
                              <span className="capitalize">{scenario.replace(/_/g, ' ')}</span>
                              <div className="flex gap-2">
                                {data.npv && <span>NPV: {formatCurrency(data.npv)}</span>}
                                {data.irr && <span>IRR: {formatPercentage(data.irr)}</span>}
                                {data.decision && (
                                  <Badge variant={data.decision === "Accept" ? "default" : "secondary"}>
                                    {data.decision}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Break-Even Analysis */}
        <TabsContent value="breakeven" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Inputs */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Target className="h-5 w-5 mr-2" />
                  Break-Even Parameters
                </CardTitle>
                <CardDescription>
                  Configure break-even analysis with sensitivity modeling
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Analysis Type</Label>
                  <Select 
                    value={breakEvenInputs.analysis_type} 
                    onValueChange={(value) => setBreakEvenInputs({...breakEvenInputs, analysis_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unit_breakeven">Unit Break-Even</SelectItem>
                      <SelectItem value="revenue_breakeven">Revenue Break-Even</SelectItem>
                      <SelectItem value="sensitivity_analysis">Sensitivity Analysis</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Fixed Costs ($)</Label>
                    <Input
                      type="number"
                      value={breakEvenInputs.fixed_costs}
                      onChange={(e) => setBreakEvenInputs({...breakEvenInputs, fixed_costs: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Variable Cost per Unit ($)</Label>
                    <Input
                      type="number"
                      value={breakEvenInputs.variable_cost_per_unit}
                      onChange={(e) => setBreakEvenInputs({...breakEvenInputs, variable_cost_per_unit: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Selling Price per Unit ($)</Label>
                    <Input
                      type="number"
                      value={breakEvenInputs.selling_price_per_unit}
                      onChange={(e) => setBreakEvenInputs({...breakEvenInputs, selling_price_per_unit: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Target Profit ($)</Label>
                    <Input
                      type="number"
                      value={breakEvenInputs.target_profit}
                      onChange={(e) => setBreakEvenInputs({...breakEvenInputs, target_profit: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                </div>

                <Button onClick={calculateBreakEven} disabled={loading} className="w-full">
                  <Calculator className="h-4 w-4 mr-2" />
                  {loading ? "Calculating..." : "Calculate Break-Even"}
                </Button>
              </CardContent>
            </Card>

            {/* Results */}
            {breakEvenResults && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Target className="h-5 w-5 mr-2" />
                    Break-Even Results
                  </CardTitle>
                  <CardDescription>
                    {breakEvenInputs.analysis_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} analysis
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {breakEvenResults.results.breakeven_units && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center p-3 bg-blue-50 rounded border">
                          <div className="text-lg font-bold text-blue-600">
                            {formatNumber(breakEvenResults.results.breakeven_units)}
                          </div>
                          <div className="text-xs text-gray-600">Break-Even Units</div>
                        </div>
                        <div className="text-center p-3 bg-green-50 rounded border">
                          <div className="text-lg font-bold text-green-600">
                            {formatCurrency(breakEvenResults.results.breakeven_revenue)}
                          </div>
                          <div className="text-xs text-gray-600">Break-Even Revenue</div>
                        </div>
                      </div>
                    )}

                    {breakEvenResults.results.contribution_margin_per_unit && (
                      <div className="p-3 bg-purple-50 rounded border">
                        <div className="flex justify-between">
                          <span>Contribution Margin:</span>
                          <span className="font-semibold">
                            {formatCurrency(breakEvenResults.results.contribution_margin_per_unit)} per unit 
                            ({formatPercentage(breakEvenResults.results.contribution_margin_percentage)})
                          </span>
                        </div>
                      </div>
                    )}

                    {breakEvenResults.results.sensitivity_scenarios && (
                      <div>
                        <h4 className="font-semibold mb-3">Sensitivity Analysis</h4>
                        <div className="max-h-40 overflow-y-auto space-y-2">
                          {Object.entries(breakEvenResults.results.sensitivity_scenarios).map(([scenario, data]) => (
                            <div key={scenario} className="flex justify-between p-2 bg-gray-50 rounded text-sm">
                              <span>{scenario} Change</span>
                              <div className="flex gap-2">
                                <span>{formatNumber(data.breakeven_units)} units</span>
                                <span>{formatCurrency(data.breakeven_revenue)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* WACC Calculation */}
        <TabsContent value="wacc" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Inputs */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Percent className="h-5 w-5 mr-2" />
                  WACC Parameters
                </CardTitle>
                <CardDescription>
                  Calculate Weighted Average Cost of Capital with CAPM integration
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Equity Value ($)</Label>
                    <Input
                      type="number"
                      value={waccInputs.equity_value}
                      onChange={(e) => setWaccInputs({...waccInputs, equity_value: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Debt Value ($)</Label>
                    <Input
                      type="number"
                      value={waccInputs.debt_value}
                      onChange={(e) => setWaccInputs({...waccInputs, debt_value: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Cost of Equity (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={waccInputs.cost_of_equity * 100}
                      onChange={(e) => setWaccInputs({...waccInputs, cost_of_equity: (parseFloat(e.target.value) || 0) / 100})}
                    />
                  </div>
                  <div>
                    <Label>Cost of Debt (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={waccInputs.cost_of_debt * 100}
                      onChange={(e) => setWaccInputs({...waccInputs, cost_of_debt: (parseFloat(e.target.value) || 0) / 100})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Tax Rate (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={waccInputs.tax_rate * 100}
                      onChange={(e) => setWaccInputs({...waccInputs, tax_rate: (parseFloat(e.target.value) || 0) / 100})}
                    />
                  </div>
                  <div>
                    <Label>Beta</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={waccInputs.beta}
                      onChange={(e) => setWaccInputs({...waccInputs, beta: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                </div>

                <Separator />
                <div className="text-sm text-gray-600 mb-2">CAPM Parameters (Optional)</div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Risk-Free Rate (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={waccInputs.risk_free_rate * 100}
                      onChange={(e) => setWaccInputs({...waccInputs, risk_free_rate: (parseFloat(e.target.value) || 0) / 100})}
                    />
                  </div>
                  <div>
                    <Label>Market Risk Premium (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={waccInputs.market_risk_premium * 100}
                      onChange={(e) => setWaccInputs({...waccInputs, market_risk_premium: (parseFloat(e.target.value) || 0) / 100})}
                    />
                  </div>
                </div>

                <Button onClick={calculateWACC} disabled={loading} className="w-full">
                  <Calculator className="h-4 w-4 mr-2" />
                  {loading ? "Calculating..." : "Calculate WACC"}
                </Button>
              </CardContent>
            </Card>

            {/* Results */}
            {waccResults && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <PieChart className="h-5 w-5 mr-2" />
                    WACC Results
                  </CardTitle>
                  <CardDescription>
                    Weighted Average Cost of Capital analysis
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="text-center p-4 bg-blue-50 rounded border">
                      <div className="text-2xl font-bold text-blue-600">
                        {formatPercentage(waccResults.results.wacc_percentage)}
                      </div>
                      <div className="text-sm text-gray-600">Weighted Average Cost of Capital</div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-3 bg-green-50 rounded border">
                        <div className="text-lg font-bold text-green-600">
                          {formatPercentage(waccResults.results.equity_weight)}
                        </div>
                        <div className="text-xs text-gray-600">Equity Weight</div>
                      </div>
                      <div className="text-center p-3 bg-purple-50 rounded border">
                        <div className="text-lg font-bold text-purple-600">
                          {formatPercentage(waccResults.results.debt_weight)}
                        </div>
                        <div className="text-xs text-gray-600">Debt Weight</div>
                      </div>
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">After-Tax Cost of Debt:</span>
                        <span className="font-medium">{formatPercentage(waccResults.results.after_tax_cost_of_debt)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Tax Shield Value:</span>
                        <span className="font-medium">{formatCurrency(waccResults.results.tax_shield)}</span>
                      </div>
                      {waccResults.results.capm_cost_of_equity && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">CAPM Cost of Equity:</span>
                          <span className="font-medium">{formatPercentage(waccResults.results.capm_cost_of_equity)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
      </div>
    </div>
  );
};

export default EnhancedFinancialCalculations;